import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import './VideoPlayer.scss';
import SvgIcons from '../../services/SvgIcons';

export default function VideoPlayer({
    src,
    poster,
    muted,
    loop,
    autoPlay = false,
    controls = true,
    hasStarted,
    setHasStarted,
    onReady = () => { },
    onTimeUpdate = () => { },
    onEnded = () => { },
    onFirstPlay = () => { },
}) {
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const playBtnRef = useRef(null);
    const pauseBtnRef = useRef(null);

    // Détection mobile
    const isAndroid = /Android/i.test(navigator.userAgent);
    // Détection du navigateur Safari
    const isSafari = /Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent);

    const options = {
        responsive: true,
        fluid: true,
        autoplay: autoPlay,
        controls: controls,
        muted: muted,
        loop: loop,
        sources: [{
            src: src,
            type: 'video/mp4'
        }],
        poster: poster,
        preload: 'metadata',
        playsinline: true,
        controlBar: {
            pictureInPictureToggle: isAndroid ? false : true // Activer le bouton PiP
        },
    };


    useEffect(() => {
        if (!playerRef.current) {
            const videoElement = document.createElement("video-js");
            if (!hasStarted) {
                videoElement.classList.add('vjs-big-play-centered');
            }
            videoRef.current.appendChild(videoElement);

            const player = playerRef.current = videojs(videoElement, options, () => {
                videojs.log('player is ready');
                onReady && onReady(player);


                if (hasStarted) {
                    // Ajouter les boutons de contrôle personnalisés avec ReactDOM.createRoot
                    const replayButtonContainer = document.createElement('div');
                    replayButtonContainer.className = "vjs-center-button vjs-center-replay";
                    player.el().appendChild(replayButtonContainer);
                    const replayRoot = ReactDOM.createRoot(replayButtonContainer);
                    replayRoot.render(<SvgIcons name='replay_10' color='#FFFFFF' />);
                    replayButtonContainer.addEventListener('click', () => {
                        player.currentTime(player.currentTime() - 10); // Recule de 10 secondes
                    });

                    const forwardButtonContainer = document.createElement('div');
                    forwardButtonContainer.className = "vjs-center-button vjs-center-forward";
                    player.el().appendChild(forwardButtonContainer);
                    const forwardRoot = ReactDOM.createRoot(forwardButtonContainer);
                    forwardRoot.render(<SvgIcons name='forward_10' color='#FFFFFF' />);
                    forwardButtonContainer.addEventListener('click', () => {
                        player.currentTime(player.currentTime() + 10); // Avance de 10 secondes
                    });

                    const removeButton = (buttonRef) => {
                        if (buttonRef.current && buttonRef.current.parentNode) {
                            buttonRef.current.remove();
                            buttonRef.current = null;
                        }
                    };

                    player.on('pause', () => {
                        if (!playerRef.current) return;

                        removeButton(pauseBtnRef);
                        const playButtonContainer = document.createElement('div');
                        playButtonContainer.className = "vjs-center-button vjs-center-play";
                        player.el().appendChild(playButtonContainer);
                        const playRoot = ReactDOM.createRoot(playButtonContainer);
                        playRoot.render(<SvgIcons name='play' color='#FFFFFF' />);
                        playButtonContainer.addEventListener('click', () => {
                            playerRef.current.play(); // Lecture
                        });
                        playBtnRef.current = playButtonContainer;
                    })

                    player.on('play', () => {
                        if (!playerRef.current) return;

                        removeButton(playBtnRef);
                        const pauseButtonContainer = document.createElement('div');
                        pauseButtonContainer.className = "vjs-center-button vjs-center-pause";
                        player.el().appendChild(pauseButtonContainer);
                        const pauseRoot = ReactDOM.createRoot(pauseButtonContainer);
                        pauseRoot.render(<SvgIcons name='pause' color='#FFFFFF' />);
                        pauseButtonContainer.addEventListener('click', () => {
                            playerRef.current.pause(); // Pause
                        });
                        pauseBtnRef.current = pauseButtonContainer;
                    })
                } else {
                    const playButtonContainer = document.createElement('div');
                    playButtonContainer.className = "vjs-center-button vjs-center-play";
                    player.el().appendChild(playButtonContainer);
                    const playRoot = ReactDOM.createRoot(playButtonContainer);
                    playRoot.render(<SvgIcons name='play' color='#FFFFFF' />);
                    playButtonContainer.addEventListener('click', () => {
                        playerRef.current.play(); // Lecture
                    });
                    playBtnRef.current = playButtonContainer;
                }

                // Détecter la première lecture
                if (isSafari) {
                    const firstPlayHandler = () => {
                        player.on('canplay', () => {
                            setHasStarted(true); // mettre à jour l'état pour indiquer la première lecture
                            onFirstPlay && onFirstPlay();
                        });
                        setTimeout(() => {
                            playerRef.current.play();
                        }, 200);
                        // Retirer le listener après la première lecture
                        player.off('play', firstPlayHandler);
                    };
                    player.on('play', firstPlayHandler);
                } else {
                    const firstPlayHandler = () => {
                        onFirstPlay && onFirstPlay();
                        setHasStarted(true); // mettre à jour l'état pour indiquer la première lecture
                        setTimeout(() => {
                            playerRef.current.play();
                        }, 200);

                        // Retirer le listener après la première lecture
                        player.off('play', firstPlayHandler);
                    };
                    player.on('play', firstPlayHandler);
                }
            });

        }

        return () => {
            if (playerRef.current) {
                playerRef.current.dispose();
                playerRef.current = null;
            }
        };
    }, [options, videoRef, onReady, onFirstPlay, setHasStarted]);

    return (
        <>
            <div data-vjs-player className='video-player'>
                <div ref={videoRef} />
            </div>
        </>
    );
};
